import { httpClient } from "../api/httpClient";
import { Endpoints, getResourceUrl } from "../constants/endpoint.constant";
import { normalizeInspectionRecord } from "./inspection.util";

const PDF_READY_IMAGE_RE = /^data:image\/(?:png|jpe?g);base64,/i;
const ANY_DATA_IMAGE_RE = /^data:image\/[a-zA-Z0-9.+-]+;base64,/i;
const JPG_OR_PNG_BLOB_RE = /^image\/(?:png|jpe?g)$/i;
const GENERIC_IMAGE_BLOB_RE = /^image\//i;

const INSPECTION_IMAGE_GROUPS = [
    "leftPics",
    "rightPics",
    "frontPics",
    "backPics",
    "windScreenPics",
    "leftFrontTirePics",
    "leftBackTirePics",
    "rightFrontTirePics",
    "rightBackTirePics",
];

const notifyProgress = (onProgress, progress) => {
    if (typeof onProgress !== "function") return;
    onProgress(progress);
};

const createProgressTracker = (onProgress, total) => {
    let completed = 0;
    const safeTotal = Math.max(1, Number(total) || 0);

    notifyProgress(onProgress, {
        completed,
        total: safeTotal,
        percent: total ? 0 : 100,
        phase: total ? "Preparing inspection images" : "No images to prepare",
    });

    return {
        step(label = "Preparing inspection images") {
            completed += 1;
            notifyProgress(onProgress, {
                completed,
                total: safeTotal,
                percent: Math.min(100, Math.round((completed / safeTotal) * 100)),
                phase: label,
            });
        },
    };
};

const getImageUri = resource => {
    if (!resource) return "";
    if (typeof resource === "string") return getResourceUrl(resource);
    return getResourceUrl(resource?.pic || resource?.image || resource?.url || resource?.uri || "");
};

const isFirebaseStorageUrl = uri => /^https:\/\/firebasestorage\.googleapis\.com\//i.test(uri);

const hasImageResource = resource => Boolean(getImageUri(resource));

const countImageCollection = images => {
    if (!Array.isArray(images)) return 0;
    return images.filter(image => hasImageResource(image?.pic || image)).length;
};

const countInspectionImages = inspect => {
    if (!inspect) return 0;
    const record = normalizeInspectionRecord(inspect);
    const singleImages = [
        record?.odometer,
        record?.licenseDiskImage,
        record?.etagImage,
        record?.driverSignature,
        record?.signature,
    ].filter(hasImageResource).length;

    return singleImages + INSPECTION_IMAGE_GROUPS.reduce(
        (count, field) => count + countImageCollection(record?.[field]),
        0
    );
};

const countTripImages = trip => Array.isArray(trip?.cancelledTrips)
    ? trip.cancelledTrips.filter(cancelledTrip => hasImageResource(cancelledTrip?.image)).length
    : 0;

const countAccidentImages = accident => countImageCollection(accident?.accidentReportPics);

const countPayloadImages = payload => {
    if (!payload) return 0;

    return [
        countTripImages(payload?.trip),
        countTripImages(payload?.tripOrder),
        countInspectionImages(payload?.inspect),
        countInspectionImages(payload?.postInspect),
        countInspectionImages(payload?.inspection?.data ?? payload?.inspection),
        countInspectionImages(payload?.postInspection?.data ?? payload?.postInspection),
        countAccidentImages(payload?.accident),
    ].reduce((sum, count) => sum + count, 0);
};

const blobToDataUrl = blob => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
});

const loadImageElement = src => new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
});

const rasterizeBlobToPng = async blob => {
    const objectUrl = URL.createObjectURL(blob);

    try {
        const image = await loadImageElement(objectUrl);
        const canvas = document.createElement("canvas");
        canvas.width = image.naturalWidth || image.width || 1;
        canvas.height = image.naturalHeight || image.height || 1;

        const context = canvas.getContext("2d");
        if (!context) return "";

        context.drawImage(image, 0, 0);
        return canvas.toDataURL("image/png");
    } finally {
        URL.revokeObjectURL(objectUrl);
    }
};

const fetchImageBlob = async uri => {
    if (isFirebaseStorageUrl(uri)) {
        const response = await httpClient.get(Endpoints.PDF_IMAGE, {
            params: { url: uri },
            responseType: "blob",
        });
        return response.data;
    }

    const response = await fetch(uri);
    if (!response.ok) return null;
    return response.blob();
};

export const toPdfCompatibleImage = async resource => {
    const uri = getImageUri(resource);
    if (!uri) return "";
    if (PDF_READY_IMAGE_RE.test(uri)) return uri;

    try {
        if (ANY_DATA_IMAGE_RE.test(uri)) {
            const response = await fetch(uri);
            const blob = await response.blob();
            return JPG_OR_PNG_BLOB_RE.test(blob.type) ? blobToDataUrl(blob) : rasterizeBlobToPng(blob);
        }

        const blob = await fetchImageBlob(uri);
        if (!blob) return uri;
        if (JPG_OR_PNG_BLOB_RE.test(blob.type)) return blobToDataUrl(blob);
        if (GENERIC_IMAGE_BLOB_RE.test(blob.type) || !blob.type || blob.type === "application/octet-stream") {
            const rasterizedImage = await rasterizeBlobToPng(blob);
            return rasterizedImage || uri;
        }
    } catch (error) {
        return uri;
    }

    return uri;
};

const mapImageCollection = async (images, key = "pic", tracker = null) => {
    if (!Array.isArray(images) || !images.length) return images;

    return Promise.all(
        images.map(async image => {
            const source = image?.[key] || image;
            const compatibleImage = await toPdfCompatibleImage(source);
            if (hasImageResource(source)) tracker?.step("Embedding inspection images");
            const imageObject = image && typeof image === "object" ? image : {};

            return {
                ...imageObject,
                [key]: compatibleImage,
                ...(key === "pic" ? { image: compatibleImage } : {}),
            };
        })
    );
};

const normalizeInspection = async (inspect, tracker = null) => {
    if (!inspect) return inspect;
    const record = normalizeInspectionRecord(inspect);
    const convertSingleImage = async resource => {
        const compatibleImage = await toPdfCompatibleImage(resource);
        if (hasImageResource(resource)) tracker?.step("Embedding inspection images");
        return compatibleImage;
    };

    const normalized = {
        ...record,
        odometer: await convertSingleImage(record?.odometer),
        licenseDiskImage: await convertSingleImage(record?.licenseDiskImage),
        etagImage: await convertSingleImage(record?.etagImage),
        driverSignature: await convertSingleImage(record?.driverSignature),
        signature: await convertSingleImage(record?.signature),
    };

    for (const field of INSPECTION_IMAGE_GROUPS) {
        normalized[field] = await mapImageCollection(record?.[field], "pic", tracker);
    }

    return normalized;
};

const normalizeTrip = async (trip, tracker = null) => {
    if (!trip) return trip;

    return {
        ...trip,
        cancelledTrips: Array.isArray(trip?.cancelledTrips)
            ? await Promise.all(
                trip.cancelledTrips.map(async cancelledTrip => {
                    const image = await toPdfCompatibleImage(cancelledTrip?.image);
                    if (hasImageResource(cancelledTrip?.image)) tracker?.step("Embedding trip images");
                    return {
                        ...cancelledTrip,
                        image,
                    };
                })
            )
            : trip?.cancelledTrips,
    };
};

const normalizeAccident = async (accident, tracker = null) => {
    if (!accident) return accident;

    return {
        ...accident,
        accidentReportPics: await mapImageCollection(accident?.accidentReportPics, "image", tracker),
    };
};

export const prepareTripPdfPayload = async (payload, options = {}) => {
    if (!payload) return payload;
    const tracker = createProgressTracker(options?.onProgress, countPayloadImages(payload));

    const normalizedInspection = payload?.inspection
        ? await normalizeInspection(payload.inspection?.data ?? payload.inspection, tracker)
        : payload?.inspection;
    const normalizedPostInspection = payload?.postInspection
        ? await normalizeInspection(payload.postInspection?.data ?? payload.postInspection, tracker)
        : payload?.postInspection;

    return {
        ...payload,
        trip: await normalizeTrip(payload?.trip, tracker),
        tripOrder: await normalizeTrip(payload?.tripOrder, tracker),
        inspect: await normalizeInspection(payload?.inspect, tracker),
        postInspect: await normalizeInspection(payload?.postInspect, tracker),
        inspection: payload?.inspection
            ? payload.inspection?.data !== undefined
                ? {
                    ...payload.inspection,
                    data: normalizedInspection,
                }
                : normalizedInspection
            : payload?.inspection,
        postInspection: payload?.postInspection
            ? payload.postInspection?.data !== undefined
                ? {
                    ...payload.postInspection,
                    data: normalizedPostInspection,
                }
                : normalizedPostInspection
            : payload?.postInspection,
        accident: await normalizeAccident(payload?.accident, tracker),
    };
};
